import { useRef, useState } from "react";
import { cn } from "../lib/utils";

interface FileUploadProps {
  onFiles?: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSizeMB?: number;
  disabled?: boolean;
  className?: string;
}

export function FileUpload({
  onFiles,
  accept,
  multiple = false,
  maxSizeMB,
  disabled = false,
  className = "",
}: FileUploadProps) {
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function process(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList);
    if (maxSizeMB !== undefined) {
      const oversized = arr.find(f => f.size > maxSizeMB * 1024 * 1024);
      if (oversized) {
        setError(`"${oversized.name}" exceeds the ${maxSizeMB} MB limit.`);
        return;
      }
    }
    setError(null);
    const next = multiple ? arr : [arr[0]];
    setSelected(next);
    onFiles?.(next);
  }

  function remove(name: string) {
    const next = selected.filter(f => f.name !== name);
    setSelected(next);
    onFiles?.(next);
  }

  return (
    <div
      className={cn("file-upload", dragging && "file-upload-dragging", disabled && "file-upload-disabled", className)}
      onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragEnter={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => {
        e.preventDefault();
        setDragging(false);
        if (!disabled) process(e.dataTransfer.files);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        className="file-upload-input"
        accept={accept}
        multiple={multiple}
        onChange={e => { process(e.target.files); e.target.value = ""; }}
        tabIndex={-1}
        aria-hidden="true"
      />

      {selected.length > 0 ? (
        <div className="file-upload-files" onClick={e => e.stopPropagation()}>
          {selected.map(f => (
            <div key={f.name} className="file-upload-file">
              <svg className="file-upload-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
              <span className="file-upload-file-name">{f.name}</span>
              <span className="file-upload-file-size">{(f.size / 1024).toFixed(0)} KB</span>
              {!disabled && (
                <button
                  type="button"
                  className="file-upload-remove"
                  onClick={() => remove(f.name)}
                  aria-label={`Remove ${f.name}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          {!disabled && (
            <p className="file-upload-change">Click to change</p>
          )}
        </div>
      ) : (
        <div className="file-upload-prompt">
          <svg className="file-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          </svg>
          <p className="file-upload-label">
            <strong>Click to upload</strong> or drag &amp; drop
          </p>
          {(accept || maxSizeMB !== undefined) && (
            <p className="file-upload-hint">
              {[accept, maxSizeMB !== undefined && `Max ${maxSizeMB} MB`].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      )}

      {error && <p className="file-upload-error" onClick={e => e.stopPropagation()}>{error}</p>}
    </div>
  );
}
